<template>
  <div class="textarea-variables-block">
    <label
      class="block my-3"
    >
      {{ t(configs.label) }}
    </label>
    <div class="p-fluid">
      <Editor
        ref="editorRef"
        :key="editorKey"
        :model-value="inputValue"
        editor-style="height: 320px"
        @text-change="handleValueChange"
        @selection-change="handleSelectionChange"
      >
        <template #toolbar>
          <span class="ql-formats">
            <select class="ql-header">
              <option value="1">{{ t('templateBuilder.configs.title1', null) }}</option>
              <option value="2">{{ t('templateBuilder.configs.title2', null) }}</option>
              <option value="3">{{ t('templateBuilder.configs.title3', null) }}</option>
              <option value="4">{{ t('templateBuilder.configs.title4', null) }}</option>
              <option
                value=""
                selected
              >{{ t('templateBuilder.configs.normal', null) }}</option>
            </select>
            <select class="ql-font ql-picker">
              <option
                value=""
                selected
              >{{ t('templateBuilder.fields.defaultFontFamily', null) }}</option>
              <option value="arial, helvetica, sans-serif">Arial</option>
              <option value="comic sans ms, cursive">Comic Sans MS</option>
              <option value="courier new, courier, monospace">Courier New</option>
              <option value="georgia, serif">Georgia</option>
              <option value="lucida sans unicode, lucida grande, sans-serif">Lucida Sans Unicode</option>
              <option value="tahoma, geneva, sans-serif">Tahoma</option>
              <option value="times new roman, times, serif">Times New Roman</option>
              <option value="trebuchet ms, helvetica, sans-serif">Trebuchet MS</option>
              <option value="verdana, geneva, sans-serif">Verdana</option>
            </select>
            <span
              v-if="showGoogleFontsButton"
              v-tooltip.top="t('googleFonts.browseButton')"
              class="ql-custom-button ql-google-fonts-btn"
              @click="openGoogleFontsModal"
            >
              <span class="ql-custom-button-icon">
                <i class="fas fa-plus" />
              </span>
            </span>
            <select class="ql-size ql-picker">
              <option
                value=""
                selected
              >{{ t('templateBuilder.fields.defaultFontSize', null) }}</option>
              <option value="8px">8</option>
              <option value="9px">9</option>
              <option value="10px">10</option>
              <option value="11px">11</option>
              <option value="12px">12</option>
              <option value="13px">13</option>
              <option value="14px">14</option>
              <option value="15px">15</option>
              <option value="16px">16</option>
              <option value="18px">18</option>
              <option value="20px">20</option>
              <option value="22px">22</option>
              <option value="24px">24</option>
              <option value="26px">26</option>
              <option value="28px">28</option>
              <option value="30px">30</option>
              <option value="32px">32</option>
              <option value="34px">34</option>
              <option value="36px">36</option>
              <option value="38px">38</option>
              <option value="40px">40</option>
              <option value="42px">42</option>
              <option value="44px">44</option>
              <option value="46px">46</option>
              <option value="48px">48</option>
              <option value="50px">50</option>
              <option value="52px">52</option>
              <option value="54px">54</option>
              <option value="56px">56</option>
              <option value="58px">58</option>
              <option value="60px">60</option>
            </select>
          </span>
          <span class="ql-formats">
            <select
              class="ql-lineheight ql-picker"
            >
              <option
                v-for="(lineHeight, index) in lineHeightList"
                :key="`lineHeight_${index}`"
                :value="lineHeight.value ?? lineHeight"
              >{{ lineHeight.label ?? lineHeight }}</option>
            </select>
            <span
              class="ql-custom-button ql-letterspacing"
              @mousedown="saveRangeSelection"
              @click="toggleLetterSpacing"
            >
              <span class="ql-custom-button-icon">
                <svg
                  height="1em"
                  viewBox="0 0 512 512"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M32 24C32 10.7 42.7 0 56 0S80 10.7 80 24V488c0 13.3-10.7 24-24 24s-24-10.7-24-24V24zM160 144c0-26.5 21.5-48 48-48h96c26.5
                    0 48 21.5 48 48V368c0 26.5-21.5 48-48 48H208c-26.5 0-48-21.5-48-48V144zM456 0c13.3 0 24 10.7 24 24V488c0 13.3-10.7
                    24-24 24s-24-10.7-24-24V24c0-13.3 10.7-24 24-24z"
                  />
                </svg>
              </span>
            </span>
            <button class="ql-bold" />
            <button class="ql-italic" />
            <button class="ql-underline" />
            <button class="ql-strike" />
            <EmojisButton
              class="ql-custom-button"
              @on-choose-emoji="insertContent"
            />
            <VariablesButton
              :variables-list="enrichedVariables"
              class="ql-custom-button"
              placeholder="T"
              @on-choose-variable="insertContent"
            />
            <AIButton
              class="input-button-ai ql-custom-button"
              custom-icon="fa-light fa-microchip-ai ql-custom-button-icon"
              :ai-selector="parserValues.selector"
              :ai-params="{ target: 'template', type: 'callback', callback: replaceContent }"
              :ai-content-type="1"
              :use-button="false"
            />
          </span>
          <span class="ql-formats">
            <span
              class="ql-custom-button"
              @click="toggleColorPicker"
            >
              <span
                class="ql-custom-button-icon"
              >
                <svg viewBox="0 0 18 18">
                  <line
                    class="ql-color-label ql-stroke ql-transparent"
                    x1="3"
                    x2="15"
                    y1="15"
                    y2="15"
                  />
                  <polyline
                    class="ql-stroke"
                    points="5.5 11 9 3 12.5 11"
                  />
                  <line
                    class="ql-stroke"
                    x1="11.63"
                    x2="6.38"
                    y1="9"
                    y2="9"
                  />
                </svg>
              </span>
            </span>
            <span
              class="ql-custom-button"
              @click="toggleColorPickerBackground"
            >
              <span
                class="ql-custom-button-icon"
              >
                <svg viewBox="0 0 18 18">
                  <g class="ql-fill ql-color-label"> <polygon points="6 6.868 6 6 5 6 5 7 5.942 7 6 6.868" /> <rect
                    height="1"
                    width="1"
                    x="4"
                    y="4"
                  /> <polygon points="6.817 5 6 5 6 6 6.38 6 6.817 5" /> <rect
                    height="1"
                    width="1"
                    x="2"
                    y="6"
                  /> <rect
                    height="1"
                    width="1"
                    x="3"
                    y="5"
                  /> <rect
                    height="1"
                    width="1"
                    x="4"
                    y="7"
                  /> <polygon points="4 11.439 4 11 3 11 3 12 3.755 12 4 11.439" /> <rect
                    height="1"
                    width="1"
                    x="2"
                    y="12"
                  /> <rect
                    height="1"
                    width="1"
                    x="2"
                    y="9"
                  /> <rect
                    height="1"
                    width="1"
                    x="2"
                    y="15"
                  /> <polygon points="4.63 10 4 10 4 11 4.192 11 4.63 10" /> <rect
                    height="1"
                    width="1"
                    x="3"
                    y="8"
                  /> <path d="M10.832,4.2L11,4.582V4H10.708A1.948,1.948,0,0,1,10.832,4.2Z" /> <path
                    d="M7,4.582L7.168,4.2A1.929,1.929,0,0,1,7.292,4H7V4.582Z"
                  /> <path
                    d="M8,13H7.683l-0.351.8a1.933,1.933,0,0,1-.124.2H8V13Z"
                  /> <rect
                    height="1"
                    width="1"
                    x="12"
                    y="2"
                  /> <rect
                    height="1"
                    width="1"
                    x="11"
                    y="3"
                  /> <path d="M9,3H8V3.282A1.985,1.985,0,0,1,9,3Z" /> <rect
                    height="1"
                    width="1"
                    x="2"
                    y="3"
                  /> <rect
                    height="1"
                    width="1"
                    x="6"
                    y="2"
                  /> <rect
                    height="1"
                    width="1"
                    x="3"
                    y="2"
                  /> <rect
                    height="1"
                    width="1"
                    x="5"
                    y="3"
                  /> <rect
                    height="1"
                    width="1"
                    x="9"
                    y="2"
                  /> <rect
                    height="1"
                    width="1"
                    x="15"
                    y="14"
                  /> <polygon
                    points="13.447 10.174 13.469 10.225 13.472 10.232 13.808 11 14 11 14 10 13.37 10 13.447 10.174"
                  /> <rect
                    height="1"
                    width="1"
                    x="13"
                    y="7"
                  /> <rect
                    height="1"
                    width="1"
                    x="15"
                    y="5"
                  /> <rect
                    height="1"
                    width="1"
                    x="14"
                    y="6"
                  /> <rect
                    height="1"
                    width="1"
                    x="15"
                    y="8"
                  /> <rect
                    height="1"
                    width="1"
                    x="14"
                    y="9"
                  /> <path d="M3.775,14H3v1H4V14.314A1.97,1.97,0,0,1,3.775,14Z" /> <rect
                    height="1"
                    width="1"
                    x="14"
                    y="3"
                  /> <polygon points="12 6.868 12 6 11.62 6 12 6.868" /> <rect
                    height="1"
                    width="1"
                    x="15"
                    y="2"
                  /> <rect
                    height="1"
                    width="1"
                    x="12"
                    y="5"
                  /> <rect
                    height="1"
                    width="1"
                    x="13"
                    y="4"
                  /> <polygon points="12.933 9 13 9 13 8 12.495 8 12.933 9" /> <rect
                    height="1"
                    width="1"
                    x="9"
                    y="14"
                  /> <rect
                    height="1"
                    width="1"
                    x="8"
                    y="15"
                  /> <path d="M6,14.926V15H7V14.316A1.993,1.993,0,0,1,6,14.926Z" /> <rect
                    height="1"
                    width="1"
                    x="5"
                    y="15"
                  /> <path d="M10.668,13.8L10.317,13H10v1h0.792A1.947,1.947,0,0,1,10.668,13.8Z" /> <rect
                    height="1"
                    width="1"
                    x="11"
                    y="15"
                  /> <path d="M14.332,12.2a1.99,1.99,0,0,1,.166.8H15V12H14.245Z" /> <rect
                    height="1"
                    width="1"
                    x="14"
                    y="15"
                  /> <rect
                    height="1"
                    width="1"
                    x="15"
                    y="11"
                  /> </g> <polyline
                    class="ql-stroke"
                    points="5.5 13 9 5 12.5 13"
                  /> <line
                    class="ql-stroke"
                    x1="11.63"
                    x2="6.38"
                    y1="11"
                    y2="11"
                  /> </svg>
              </span>
            </span>

            <button
              class="ql-list"
              value="ordered"
            />
            <button
              class="ql-list"
              value="bullet"
            />
            <select class="ql-align">
              <option
                value="left"
                selected
              />
              <option value="center" />
              <option value="right" />
              <option value="justify" />
            </select>
          </span>
          <span class="ql-formats">
            <button class="ql-link" />
            <button class="ql-code-block" />
            <button class="ql-clean" />
          </span>
        </template>
      </Editor>
      <OverlayPanel
        ref="overlayPanelRef"
      >
        <CustomColorPicker
          data-test-id="color-picker"
          color="#000000"
          :color-format="colorFormat"
          title="Color Picker"
          @color-changed="changeColor"
        />
      </OverlayPanel>
      <OverlayPanel
        ref="overlayPanelBackgroundRef"
      >
        <CustomColorPicker
          color="#000000"
          :color-format="colorFormat"
          title="Color Picker"
          @color-changed="changeColorBackground"
        />
      </OverlayPanel>
      <OverlayPanel
        ref="overlayPanelLetterSpacingRef"
      >
        <BaseInputNumber
          v-model="letterSpacingValue"
          suffix="px"
          @update:model-value="changeLetterSpacing"
        />
      </OverlayPanel>
      <GoogleFontPickerModal
        v-if="showGoogleFontsButton"
        v-model:visible="isGoogleFontsModalVisible"
        :current-font="currentGoogleFontInQuill"
        :is-email-template="isEmailTemplateType"
        :iframe-document="iframeDocumentForModal"
        @select="handleGoogleFontSelect"
      />
      <WysiwygLinkDialog
        :visible="isLinkDialogVisible"
        :initial-url="linkDialogInitialUrl"
        :initial-text="linkDialogInitialText"
        :show-funnel-option="showFunnelOption"
        :variables-list="enrichedVariables"
        @update:visible="isLinkDialogVisible = $event"
        @confirm="handleLinkConfirm"
        @remove="handleLinkRemove"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  PropType,
  Ref,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Editor from 'primevue/editor';
import {
  ColorFormatEnum,
  FieldConfig,
  ParserFieldObject,
  Property,
} from '@/types';
import EmojisButton from '@/components/fields/partials/EmojisButton.vue';
import VariablesButton from '@/components/fields/partials/VariablesButton.vue';
import Quill, { applyLinkToRange, FontStyle } from '@/components/template-builder/utils/quill-setup';
import CustomColorPicker from '@/components/fields/partials/CustomColorPicker.vue';
import OverlayPanel from 'primevue/overlaypanel';
import Tooltip from 'primevue/tooltip';
import { getLineHeightOptions } from '@/configs/line-height';
import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';
import AIButton from '@/components/fields/AIButton.vue';
import GoogleFontPickerModal from '@/components/modals/GoogleFontPickerModal.vue';
import WysiwygLinkDialog from '@/components/template-builder/fields/WysiwygLinkDialog.vue';
import { useEditorBackground } from '@/composables/useEditorBackground';
import { getTemplateIframeDocument, isDisplayTemplate, isEmailTemplate } from '@/components/template-builder/utils/helpers';
import { TemplateEditorState, refreshNextSteps, updateTemplateConfigurationNextIdTemplate } from '@/composables/template-editor/TemplateEditor';
import { useGoogleFonts } from '@/composables/google-fonts/useGoogleFonts';
import { getVariablesWithCollectionContext, getMetadataVariables } from '@/composables/template-builder/collectionContext';
import { getCustomContactProperties } from '@/configs/custom-contact-properties';

export default defineComponent({
  name: 'WysiwygEditor',
  components: {
    AIButton,
    BaseInputNumber,
    OverlayPanel,
    CustomColorPicker,
    VariablesButton,
    EmojisButton,
    Editor,
    GoogleFontPickerModal,
    WysiwygLinkDialog,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties);
    const inputValue: Ref<string> = ref(properties.value[0].value ?? props.configs.options.defaultValue);
    const editorRef = ref();
    const editorKey = ref(0);
    const overlayPanelRef = ref();
    const overlayPanelBackgroundRef = ref();
    const overlayPanelLetterSpacingRef = ref();
    const colorFormat: Ref<string> = ref(ColorFormatEnum.HEX);
    const lineHeightOptions = getLineHeightOptions(t);
    lineHeightOptions.unshift({
      label: t('templateBuilder.fields.defaultLineHeight'),
      value: '',
    });
    const lineHeightList = ref(lineHeightOptions);
    const letterSpacingValue = ref();
    const selectedRange = ref();
    const { registerEditor, unregisterEditor } = useEditorBackground();
    const enrichedVariables = ref(props.configs.options.variablesList || []);

    // Google Fonts integration
    const isGoogleFontsModalVisible = ref(false);
    const {
      injectFontToIframe,
      getDetectedTemplateFonts,
      isEmailSafe,
    } = useGoogleFonts();

    const showGoogleFontsButton = computed(() => {
      const templateType = TemplateEditorState?.template?.type;
      return isDisplayTemplate(templateType) || isEmailTemplate(templateType);
    });

    const isEmailTemplateType = computed(() => {
      const templateType = TemplateEditorState?.template?.type;
      return isEmailTemplate(templateType);
    });

    const iframeDocumentForModal = computed(() => {
      try {
        return getTemplateIframeDocument() || null;
      } catch {
        return null;
      }
    });

    const currentGoogleFontInQuill = computed(() => {
      if (!editorRef.value?.quill) return '';
      const format = editorRef.value.quill.getFormat();
      const fontValue = format?.font || '';
      if (!fontValue) return '';
      const match = fontValue.match(/"([^"]+)"/);
      return match ? match[1] : '';
    });

    const openGoogleFontsModal = () => {
      isGoogleFontsModalVisible.value = true;
    };

    // Saved Quill selection for Google Fonts picker (selection is lost when clicking dropdown items)
    const savedFontPickerRange = ref<{ index: number; length: number } | null>(null);

    // Override canAdd to bypass Parchment's Registry.query node check.
    // Parchment's default canAdd first checks Registry.query(node, scope) which can
    // return null in certain contexts (e.g. when formatText is called programmatically),
    // causing the format to silently fail even when the value is in the whitelist.
    // We keep the whitelist validation but skip the node scope check.
    const origCanAdd = FontStyle.canAdd;
    FontStyle.canAdd = function patchedCanAdd(node: HTMLElement, value: any): boolean {
      // Try the original check first (works for standard Quill dropdown fonts)
      const origResult = origCanAdd.call(FontStyle, node, value);
      if (origResult) return true;
      // Fallback: if the node check failed but the value is in the whitelist, allow it
      if (FontStyle.whitelist != null && typeof value === 'string') {
        return FontStyle.whitelist.indexOf(value) > -1;
      }
      return false;
    };

    // Normalize a font-family CSS value through browser round-trip.
    // Browsers normalize quotes, spacing, casing when setting style.fontFamily.
    // Quill's StyleAttributor reads back from DOM and compares with whitelist,
    // so the whitelist value MUST match the browser-normalized form.
    const normalizeFontValue = (fontValue: string): string => {
      const tempSpan = document.createElement('span');
      tempSpan.style.fontFamily = fontValue;
      return tempSpan.style.fontFamily || fontValue;
    };

    // Apply a Google Font format to the saved selection
    const applyGoogleFont = (fontValue: string): void => {
      const quill = editorRef.value?.quill;
      if (!quill) return;

      const range = savedFontPickerRange.value || quill.getSelection();
      if (!range) return;

      // Use browser-normalized value so it matches what Quill reads back from DOM
      const normalized = normalizeFontValue(fontValue);

      // Ensure the normalized value is in the whitelist
      if (FontStyle.whitelist && !FontStyle.whitelist.includes(normalized)) {
        FontStyle.whitelist.push(normalized);
      }

      quill.focus();
      quill.setSelection(range.index, range.length, 'silent');

      if (range.length > 0) {
        quill.formatText(range.index, range.length, 'font', normalized, 'user');
      } else {
        quill.format('font', normalized);
      }

      handleValueChange();
    };

    // Add a Google Font to Quill's font picker DOM and whitelist
    const addFontToQuillPicker = (fontFamily: string, fontValue: string): void => {
      if (!editorRef.value?.quill) return;

      // Normalize through browser round-trip so value matches what Quill reads back from DOM
      const normalized = normalizeFontValue(fontValue);

      // Add to FontStyle whitelist if not already present
      if (FontStyle.whitelist && !FontStyle.whitelist.includes(normalized)) {
        FontStyle.whitelist.push(normalized);
      }

      // Find the Quill font picker in the toolbar DOM
      const editorEl = editorRef.value.$el;
      if (!editorEl) return;

      const fontPicker = editorEl.querySelector('.ql-font.ql-picker');
      if (!fontPicker) return;

      // Check if already added to picker DOM
      const pickerOptions = fontPicker.querySelector('.ql-picker-options');
      if (!pickerOptions) return;

      const escapedValue = normalized.replace(/"/g, '\\"');
      const existingItem = pickerOptions.querySelector(`.ql-picker-item[data-value="${escapedValue}"]`);
      if (existingItem) return;

      // Create new picker item in the custom dropdown
      const newItem = document.createElement('span');
      newItem.classList.add('ql-picker-item');
      newItem.setAttribute('data-value', normalized);
      newItem.setAttribute('data-label', fontFamily);
      newItem.setAttribute('tabindex', '0');
      newItem.setAttribute('role', 'button');

      // Save selection on mousedown (before focus is lost)
      newItem.addEventListener('mousedown', () => {
        const quill = editorRef.value?.quill;
        if (quill) {
          const range = quill.getSelection();
          if (range) {
            savedFontPickerRange.value = { index: range.index, length: range.length };
          }
        }
      });

      // Apply font on click with saved selection
      newItem.addEventListener('click', () => {
        // Update picker UI: remove ql-selected from others, mark this one
        pickerOptions.querySelectorAll('.ql-picker-item').forEach((el: Element) => el.classList.remove('ql-selected'));
        newItem.classList.add('ql-selected');

        // Update picker label
        const pickerLabel = fontPicker.querySelector('.ql-picker-label');
        if (pickerLabel) {
          pickerLabel.setAttribute('data-value', normalized);
          pickerLabel.setAttribute('data-label', fontFamily);
        }

        // Close the picker dropdown
        fontPicker.classList.remove('ql-expanded');

        // Apply font format with saved selection
        applyGoogleFont(normalized);
      });

      pickerOptions.appendChild(newItem);
    };

    // Handle Google Font selection from the modal
    const handleGoogleFontSelect = (fontData: { family: string; value: string; isEmailSafe: boolean }) => {
      if (!editorRef.value?.quill) return;

      const { family, value: fontValue } = fontData;

      // Add to Quill picker DOM and whitelist
      addFontToQuillPicker(family, fontValue);

      // Inject font CSS into the template iframe
      const iframeDoc = getTemplateIframeDocument();
      if (iframeDoc && !fontData.isEmailSafe) {
        injectFontToIframe(family, iframeDoc);
      }

      // Also inject into parent document for Quill editor preview
      if (!fontData.isEmailSafe) {
        injectFontToIframe(family, document);
      }

      // Apply the font with saved selection
      applyGoogleFont(fontValue);
    };

    const cleanTrailingEmptyBlock = (html: string): string => html.replace(/<p><br><\/p>\s*$/, '');

    let pendingValueChange: number | null = null;

    const applyValueChange = () => {
      pendingValueChange = null;
      const quill = editorRef.value?.quill;
      if (!quill) return;
      // Repair anchor hrefs that the browser may have normalized (e.g. drag & drop
      // turns href="{var=...}" into an absolute URL). data-spm-href holds the raw value.
      quill.root.querySelectorAll('a[data-spm-href]').forEach((a: HTMLAnchorElement) => {
        const clean = a.getAttribute('data-spm-href');
        if (clean && a.getAttribute('href') !== clean) {
          a.setAttribute('href', clean);
        }
      });
      const content = cleanTrailingEmptyBlock(quill.root.innerHTML);
      const updatedProperties: Array<Property> = properties.value.map((prop: Property) => {
        const updatedProperty = prop;
        updatedProperty.value = content;
        return updatedProperty;
      });
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    // Debounced: the per-keystroke template update pipeline is heavy. Never write
    // inputValue back here — changing the Editor's modelValue prop makes PrimeVue
    // rebuild the whole Quill document while unfocused, corrupting typing (SC-3791)
    const handleValueChange = () => {
      if (pendingValueChange !== null) window.clearTimeout(pendingValueChange);
      pendingValueChange = window.setTimeout(applyValueChange, 150);
    };

    const flushValueChange = () => {
      if (pendingValueChange === null) return;
      window.clearTimeout(pendingValueChange);
      applyValueChange();
    };

    // Blur flushes pending edits so save / widget switch read up-to-date state
    const handleSelectionChange = (event: { range: unknown }) => {
      if (!event.range) flushValueChange();
    };

    const insertContent = (value: string | number | undefined) => {
      editorRef.value.quill.insertText(editorRef.value.quill.selection.savedRange.index, value);
      handleValueChange();
    };

    const replaceContent = (value: string) => {
      editorRef.value.quill.clipboard.dangerouslyPasteHTML(value);
      handleValueChange();
    };

    const toggleColorPicker = (event: MouseEvent) => {
      overlayPanelRef.value.toggle(event);
    };

    const toggleColorPickerBackground = (event: MouseEvent) => {
      overlayPanelBackgroundRef.value.toggle(event);
    };

    const changeColor = (color: string) => {
      editorRef.value.quill.format('color', color);
      handleValueChange();
    };

    const changeColorBackground = (color: string) => {
      editorRef.value.quill.format('background-color', color);
      handleValueChange();
    };
    const saveRangeSelection = (event: MouseEvent) => {
      // Store selected range (because when opening the panel, the range disappears)
      const range = editorRef.value.quill.getSelection();
      if (range !== null) {
        selectedRange.value = range;
      }
    };
    const toggleLetterSpacing = (event: MouseEvent) => {
      letterSpacingValue.value = null;
      overlayPanelLetterSpacingRef.value.toggle(event);
      editorRef.value.quill.setSelection(selectedRange.value);
    };

    const changeLetterSpacing = (value: any) => {
      if (selectedRange.value) {
        editorRef.value.quill.setSelection(selectedRange.value);
        editorRef.value.quill.formatText(selectedRange.value.index, selectedRange.value.length, {
          'letter-spacing': `${value}px`,
        });
      }

      handleValueChange();
    };

    // ---- Custom Link Dialog ----

    const isLinkDialogVisible = ref(false);
    const linkDialogInitialUrl = ref('');
    const linkDialogInitialText = ref('');
    const savedLinkRange = ref<{ index: number; length: number } | null>(null);

    // Show funnel option for email and display templates
    const showFunnelOption = computed(() => {
      const templateType = TemplateEditorState?.template?.type;
      return isDisplayTemplate(templateType) || isEmailTemplate(templateType);
    });

    // Custom link handler — replaces Quill's default tooltip
    const openLinkDialog = () => {
      const quill = editorRef.value?.quill;
      if (!quill) return;

      const range = quill.getSelection();
      if (!range) return;

      savedLinkRange.value = { index: range.index, length: range.length };

      // Detect existing link on selection
      const format = quill.getFormat(range);
      linkDialogInitialUrl.value = (typeof format?.link === 'string') ? format.link : '';
      linkDialogInitialText.value = range.length > 0 ? quill.getText(range.index, range.length) : '';

      isLinkDialogVisible.value = true;
    };

    const handleLinkConfirm = (result: { type: string; url: string; text: string }) => {
      const quill = editorRef.value?.quill;
      if (!quill || !savedLinkRange.value) return;

      const fallbackText = result.type === 'funnel' ? '🔗' : result.url;
      applyLinkToRange(quill, savedLinkRange.value, result.url, result.text ?? '', fallbackText);

      handleValueChange();

      // Sync funnel state if it's a redirect link
      // Use nextTick + setTimeout so the iframe DOM is updated before scanning
      if (result.url.includes('redirect_')) {
        nextTick(() => {
          setTimeout(() => refreshNextSteps(), 300);
        });
      }
    };

    const handleLinkRemove = () => {
      const quill = editorRef.value?.quill;
      if (!quill || !savedLinkRange.value) return;

      const hadRedirect = linkDialogInitialUrl.value.includes('redirect_');
      const { index, length } = savedLinkRange.value;

      quill.formatText(index, length, 'link', false, 'silent');

      handleValueChange();

      // Resync funnel if we removed a redirect link
      if (hadRedirect) {
        nextTick(() => {
          setTimeout(() => refreshNextSteps(), 300);
        });
      }
    };

    const checkInterval = ref();

    onBeforeMount(() => {
      editorKey.value += 1;

      // Avoids having an empty history point
      checkInterval.value = window.setInterval(() => {
        if (editorRef.value && editorRef.value.quill) {
          editorRef.value.quill.history.clear();
          window.clearInterval(checkInterval.value);
        }
      }, 200);
    });

    onMounted(async () => {
      const interval = setInterval(() => {
        const quillInstance = editorRef.value?.quill;
        if (quillInstance?.root) {
          // Override Quill's default link handler with our custom dialog
          const toolbar = quillInstance.getModule('toolbar');
          if (toolbar) {
            toolbar.addHandler('link', () => {
              openLinkDialog();
            });
          }

          // Suppress Quill's native link tooltip entirely — we rely on explicit
          // clicks (below) and the toolbar link icon to edit links
          const quillTooltip = quillInstance.theme?.tooltip;
          if (quillTooltip) {
            quillTooltip.show = () => { /* no-op */ };
          }

          // Click on an <a> inside the editor → open our dialog
          quillInstance.root.addEventListener('click', (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            const anchor = target?.closest('a');
            if (!anchor || !quillInstance.root.contains(anchor)) return;

            event.preventDefault();
            event.stopPropagation();

            const blot = (Quill as any).find(anchor);
            if (!blot) return;
            const blotIndex = quillInstance.getIndex(blot);
            const blotLength = blot.length();

            savedLinkRange.value = { index: blotIndex, length: blotLength };
            linkDialogInitialUrl.value = anchor.getAttribute('data-spm-href') || anchor.getAttribute('href') || '';
            linkDialogInitialText.value = quillInstance.getText(blotIndex, blotLength);
            isLinkDialogVisible.value = true;
          });

          const { selector } = props.parserValues;
          const template = getTemplateIframeDocument();
          const refEl = template.querySelector(selector) as HTMLElement;
          registerEditor(editorRef.value?.quill?.root, refEl);
          const { applyBackgroundReferenceFromSelector } = useEditorBackground();
          applyBackgroundReferenceFromSelector(selector, template);

          // Add detected Google Fonts to Quill picker
          if (showGoogleFontsButton.value) {
            const detectedFonts = getDetectedTemplateFonts();
            detectedFonts.forEach((fontOption) => {
              addFontToQuillPicker(fontOption.label, fontOption.value);
              // Inject into parent document for Quill editor preview
              if (!isEmailSafe(fontOption.label)) {
                injectFontToIframe(fontOption.label, document);
              }
            });

            // Scan Quill content for inline Google Fonts not yet in whitelist
            const allElements = quillInstance.root.querySelectorAll('*');
            allElements.forEach((el: HTMLElement) => {
              const fontFamily = el.style?.fontFamily;
              if (fontFamily && FontStyle.whitelist && !FontStyle.whitelist.includes(fontFamily)) {
                const match = fontFamily.match(/"([^"]+)"/);
                if (match) {
                  const familyName = match[1];
                  if (!isEmailSafe(familyName)) {
                    addFontToQuillPicker(familyName, fontFamily);
                    if (template) {
                      injectFontToIframe(familyName, template);
                    }
                    injectFontToIframe(familyName, document);
                  }
                }
              }
            });
          }

          clearInterval(interval);
        }
      }, 100);

      let baseVariables = props.configs.options.variablesList || [];

      // Add custom contact properties to base variables (for use outside loops)
      try {
        const customSchemas = await getCustomContactProperties();
        const customVariables = customSchemas.flatMap((schema) => schema.properties.map((prop) => ({
          value: `{var=${prop.variableName}}`,
          label: `${schema.schemaName} - ${prop.name}`,
          labelWithBadge: `${schema.schemaName} - ${prop.name} <span class="custom-badge-inline">CUSTOM</span>`,
          type: prop.type,
          isCustom: true,
        })));
        baseVariables = [...baseVariables, ...customVariables];
      } catch (error) {
        console.error('Error loading custom contact properties:', error);
      }

      // Add metadata variables (custom events + game)
      try {
        const metadataVars = await getMetadataVariables();
        baseVariables = [...baseVariables, ...metadataVars];
      } catch (error) {
        console.error('Error loading metadata variables:', error);
      }

      // Enrich variables with collection context
      if (props.parserValues.selector) {
        enrichedVariables.value = await getVariablesWithCollectionContext(
          props.parserValues.selector,
          baseVariables,
        ) as never[];
      } else {
        enrichedVariables.value = baseVariables as never[];
      }
    });

    // Nettoyage avant démontage
    onBeforeUnmount(() => {
      flushValueChange();

      const el = editorRef.value?.quill?.root;
      if (el) unregisterEditor(el);

      if (editorRef.value) {
        if (editorRef.value.quill) {
          editorRef.value.quill = null;
        }
        editorRef.value = null;
      }

      if (overlayPanelRef.value) {
        overlayPanelRef.value = null;
      }
      if (overlayPanelBackgroundRef.value) {
        overlayPanelBackgroundRef.value = null;
      }
      if (overlayPanelLetterSpacingRef.value) {
        overlayPanelLetterSpacingRef.value = null;
      }

      if (checkInterval.value) {
        window.clearInterval(checkInterval.value);
      }
    });

    return {
      t,
      inputValue,
      enrichedVariables,
      editorRef,
      editorKey,
      overlayPanelRef,
      overlayPanelBackgroundRef,
      overlayPanelLetterSpacingRef,
      colorFormat,
      lineHeightList,
      letterSpacingValue,
      changeColor,
      toggleColorPicker,
      changeColorBackground,
      toggleColorPickerBackground,
      handleValueChange,
      handleSelectionChange,
      insertContent,
      replaceContent,
      saveRangeSelection,
      toggleLetterSpacing,
      changeLetterSpacing,
      // Google Fonts
      showGoogleFontsButton,
      isGoogleFontsModalVisible,
      isEmailTemplateType,
      iframeDocumentForModal,
      currentGoogleFontInQuill,
      openGoogleFontsModal,
      handleGoogleFontSelect,
      // Link Dialog
      isLinkDialogVisible,
      linkDialogInitialUrl,
      linkDialogInitialText,
      showFunnelOption,
      handleLinkConfirm,
      handleLinkRemove,
    };
  },
});
</script>

<style scoped lang="scss">
:deep() .p-togglebutton.p-button.emoji-button,
:deep() .variable-dropdown {
  font-size: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  display: inline-block !important;
  float: left;
  height: 24px;
  padding: 1px 0 !important;
  width: 28px;
  filter: grayscale(100%);
  .p-dropdown-trigger {
    display: none;
  }
  .p-dropdown-label {
    display: inline;
  }
}
</style>

<style lang="scss">
.ql-custom-button {
  width: 28px!important;
  color: #444;
  display: inline-block!important;
  float: left;
  font-size: 14px;
  font-weight: 500;
  height: 24px!important;
  position: relative;
  vertical-align: middle;
  cursor: pointer;
}

.ql-custom-button .ql-custom-button-icon {
  padding: 2px 4px!important;
  display: inline-block;
  width: 100%!important;
  height: 100%!important;
}
.ql-custom-button .ql-custom-button-icon.fa-microchip-ai {
  vertical-align: top;
  font-size:18px;
}

.p-editor-container .ql-snow.ql-toolbar button.ql-active,
.p-editor-container .ql-snow.ql-toolbar .ql-picker-label.ql-active,
.p-editor-container .ql-snow.ql-toolbar .ql-picker-item.ql-selected  {
  color: $brand-color-primary;
}

.p-editor-container .ql-snow.ql-toolbar button.ql-active .ql-stroke,
.p-editor-container .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke,
.p-editor-container .ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke {
  stroke: $brand-color-primary;
}

.ql-snow .ql-formats .ql-picker.ql-header {
  width: 80px;
}

.ql-snow .ql-formats .ql-picker.ql-font {
  width: 165px;

  .ql-picker-options {
    max-height: 250px;
    overflow-y: auto;
  }
}

.ql-snow .ql-formats .ql-google-fonts-btn {
  width: 22px !important;
  height: 24px !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  float: left;
  cursor: pointer;
  color: #6c757d;
  vertical-align: middle;
  margin-left: -4px;
  font-size: 11px;

  &:hover {
    color: $brand-color-primary;
  }

  .ql-custom-button-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
}

.ql-snow .ql-formats .ql-picker.ql-size {
  width: 75px;

  .ql-picker-options {
    max-height: 200px;
    overflow-y: auto;
  }
}

.ql-snow .ql-formats .ql-picker.ql-lineheight {
  width: 25px;
  padding: 0px 5px;

  .ql-picker-label {
    padding-left: 0 !important;

    &:after {
      color: #6c757d !important;
      content: '\f871';
      font-family: 'Font Awesome 6 Pro';
      position: absolute;
      top: 4px !important;
      right: 0;
    }

    & > svg {
      display: none;
    }
  }

  .ql-picker-item:before {
    content: attr(data-label);
  }
}

.ql-snow .ql-formats .ql-custom-button.ql-letterspacing {
  width: 20px;
  padding: 3px 0px;
  position: relative;

  & svg {
    fill: #6c757d;
  }
}
</style>
